import { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { Project, Issue, User, Sprint, Board, Message, AssignmentHistory, ChatMessage, IssueChat, SprintStats } from '../types';
import { users as fallbackUsers } from '../data/mockData';

export const useJira = (isAuthenticated: boolean = false) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [chats, setChats] = useState<IssueChat[]>([]);
  const [users, setUsers] = useState<User[]>(fallbackUsers);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load initial data from API only when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setIsLoading(true);
        console.log('useJira: Loading initial data...');

        // Load projects, sprints, and users
        const [projectsData, sprintsData, usersData] = await Promise.all([
          apiService.getProjects(),
          apiService.getSprints(),
          apiService.getUsers()
        ]);

        // Attach team member ids to each project
        const projectsWithMembers = await Promise.all(
          projectsData.map(async (project) => {
            try {
              const members = await apiService.getProjectMembers(project.id);
              return {
                ...project,
                startDate: project.start_date,
                endDate: project.end_date,
                createdAt: project.created_at,
                updatedAt: project.updated_at,
                teamMembers: members.map(m => m.id),
              };
            } catch (error) {
              console.warn('useJira: Failed to load project members', error);
              return {
                ...project,
                startDate: project.start_date,
                endDate: project.end_date,
                createdAt: project.created_at,
                updatedAt: project.updated_at,
                teamMembers: [],
              };
            }
          })
        );

        console.log('useJira: Data loaded successfully', {
          projects: projectsWithMembers.length,
          sprints: sprintsData.length,
          users: usersData.length
        });

        setProjects(projectsWithMembers);
        setSprints(sprintsData);
        setUsers(usersData);

        // Get current user (will use cached data if available)
        try {
          const userData = await apiService.getCurrentUser();
          setCurrentUser(userData);
          console.log('useJira: Current user data loaded');
        } catch (error) {
          console.warn('useJira: Failed to load current user, using fallback');
          // Don't fail the entire load if user data fails
        }

        setError(null);
      } catch (err) {
        console.error('Failed to load data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isAuthenticated]);

  const addSprint = async (sprintData: Omit<Sprint, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      console.log('useJira: Creating sprint');
      const newSprint = await apiService.createSprint(sprintData);
      setSprints(prev => [...prev, newSprint]);
      console.log('useJira: Sprint created successfully');
      return newSprint;
    } catch (error) {
      console.error('Failed to create sprint:', error);
      throw error;
    }
  };

  const updateSprint = async (id: string, updates: Partial<Sprint>) => {
    try {
      console.log('useJira: Updating sprint:', id);
      const updatedSprint = await apiService.updateSprint(id, updates);
      setSprints(prev => prev.map(sprint =>
        sprint.id === id ? updatedSprint : sprint
      ));
      console.log('useJira: Sprint updated successfully');
    } catch (error) {
      console.error('Failed to update sprint:', error);
      throw error;
    }
  };

  const deleteSprint = async (id: string) => {
    try {
      console.log('useJira: Deleting sprint:', id);
      // Note: Add delete endpoint to API service if needed
      setSprints(prev => prev.filter(sprint => sprint.id !== id));
    } catch (error) {
      console.error('Failed to delete sprint:', error);
      throw error;
    }
  };

  const startSprint = async (id: string) => {
    try {
      console.log('useJira: Starting sprint:', id);
      await apiService.startSprint(id);
      // Reload sprints to get updated state
      const updatedSprints = await apiService.getSprints();
      setSprints(updatedSprints);
      console.log('useJira: Sprint started successfully');
    } catch (error) {
      console.error('Failed to start sprint:', error);
      throw error;
    }
  };

  const completeSprint = async (id: string) => {
    try {
      console.log('useJira: Completing sprint:', id);
      await apiService.completeSprint(id);
      // Reload sprints to get updated state
      const updatedSprints = await apiService.getSprints();
      setSprints(updatedSprints);
      console.log('useJira: Sprint completed successfully');
    } catch (error) {
      console.error('Failed to complete sprint:', error);
      throw error;
    }
  };

  const addIssueToSprint = async (sprintId: string, issueId: string) => {
    try {
      console.log('useJira: Adding issue to sprint:', { sprintId, issueId });
      await apiService.addIssueToSprint(sprintId, issueId);
      // Reload data to reflect changes
      const [updatedSprints, updatedIssues] = await Promise.all([
        apiService.getSprints(),
        apiService.getIssues()
      ]);
      setSprints(updatedSprints);
      setIssues(updatedIssues);
      console.log('useJira: Issue added to sprint successfully');
    } catch (error) {
      console.error('Failed to add issue to sprint:', error);
      throw error;
    }
  };

  const removeIssueFromSprint = async (sprintId: string, issueId: string) => {
    try {
      console.log('useJira: Removing issue from sprint:', { sprintId, issueId });
      await apiService.removeIssueFromSprint(sprintId, issueId);
      // Reload data to reflect changes
      const [updatedSprints, updatedIssues] = await Promise.all([
        apiService.getSprints(),
        apiService.getIssues()
      ]);
      setSprints(updatedSprints);
      setIssues(updatedIssues);
      console.log('useJira: Issue removed from sprint successfully');
    } catch (error) {
      console.error('Failed to remove issue from sprint:', error);
      throw error;
    }
  };

  const getSprintStats = async (sprintId: string): Promise<SprintStats> => {
    try {
      return await apiService.getSprintStats(sprintId);
    } catch (error) {
      console.error('Failed to get sprint stats:', error);
      return {
        totalIssues: 0,
        completedIssues: 0,
        inProgressIssues: 0,
        todoIssues: 0,
        totalStoryPoints: 0,
        completedStoryPoints: 0,
        remainingStoryPoints: 0,
        velocityTrend: []
      };
    }
  };

  const addProject = async (projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      console.log('useJira: Creating project');
      const newProject = await apiService.createProject(projectData);
      setProjects(prev => [...prev, newProject]);
      console.log('useJira: Project created successfully');
      return newProject;
    } catch (error) {
      console.error('Failed to create project:', error);
      throw error;
    }
  };

  const updateProject = async (id: string, updates: Partial<Project>) => {
    try {
      console.log('useJira: Updating project:', id);
      const updatedProject = await apiService.updateProject(id, updates);
      setProjects(prev => prev.map(project =>
        project.id === id ? updatedProject : project
      ));
      console.log('useJira: Project updated successfully');
    } catch (error) {
      console.error('Failed to update project:', error);
      throw error;
    }
  };

  const deleteProject = async (id: string) => {
    try {
      console.log('useJira: Deleting project:', id);
      await apiService.deleteProject(id);
      setProjects(prev => prev.filter(project => project.id !== id));
      setIssues(prev => prev.filter(issue => issue.projectId !== id));
      console.log('useJira: Project deleted successfully');
    } catch (error) {
      console.error('Failed to delete project:', error);
      throw error;
    }
  };

  const addIssue = async (issueData: Omit<Issue, 'id' | 'key' | 'createdAt' | 'updatedAt' | 'subtasks' | 'attachments' | 'comments' | 'worklog' | 'assignmentHistory' | 'chatId'>) => {
    try {
      console.log('useJira: Creating issue with data:', issueData);
      const newIssue = await apiService.createIssue(issueData);
      console.log('useJira: Issue created successfully:', newIssue);
      setIssues(prev => [...prev, newIssue]);
      return newIssue;
    } catch (error) {
      console.error('Failed to create issue:', error);
      throw error;
    }
  };

  const getIssuesByProject = async (projectId: string) => {
    try {
      console.log('Fetching issues for project:', projectId);
      // Use optimized API call with minimal fields for Kanban
      const projectIssues = await apiService.getProjectIssues(projectId);
      console.log('Fetched project issues:', projectIssues);

      // Convert API format to frontend format
      const convertedIssues = projectIssues.map(apiIssue => ({
        id: apiIssue.id,
        key: apiIssue.key,
        projectId: apiIssue.project_id,
        summary: apiIssue.summary,
        description: apiIssue.description || '', // May not be included in minimal response
        type: apiIssue.type,
        status: apiIssue.status,
        priority: apiIssue.priority,
        assignee: apiIssue.assignee,
        assignees: apiIssue.assignees || [],
        assignees_users: apiIssue.assignees_users || [],
        reporter: apiIssue.reporter,
        labels: apiIssue.labels || [], // May not be included in minimal response
        components: [],
        fixVersions: [],
        storyPoints: apiIssue.story_points,
        estimationTime: apiIssue.estimation_time,
        createdAt: apiIssue.created_at,
        updatedAt: apiIssue.updated_at,
        dueDate: apiIssue.due_date,
        subtasks: [],
        attachments: [],
        comments: [],
        worklog: [],
        assignmentHistory: []
      }));

      // Update local issues state with project-specific issues
      setIssues(prev => [
        ...prev.filter(issue => issue.projectId !== projectId),
        ...convertedIssues
      ]);

      return convertedIssues;
    } catch (error) {
      console.error('Failed to fetch project issues:', error);
      // Fallback to local filtering
      return issues.filter(issue => issue.projectId === projectId);
    }
  };

  const updateIssue = async (id: string, updates: Partial<Issue>) => {
    // Optimistically update local state immediately so UI reflects the change
    setIssues(prev => prev.map(issue =>
      issue.id === id ? { ...issue, ...updates } : issue
    ));

    try {
      console.log('useJira: Updating issue:', id);
      const apiIssue = await apiService.updateIssue(id, updates);

      // Convert API response (snake_case) to frontend format (camelCase)
      // Merge with existing issue data to preserve fields the API doesn't return
      setIssues(prev => prev.map(issue => {
        if (issue.id !== id) return issue;
        return {
          ...issue,
          ...updates,
          ...(apiIssue.project_id !== undefined ? { projectId: apiIssue.project_id } : {}),
          ...(apiIssue.summary !== undefined ? { summary: apiIssue.summary } : {}),
          ...(apiIssue.description !== undefined ? { description: apiIssue.description } : {}),
          ...(apiIssue.type !== undefined ? { type: apiIssue.type } : {}),
          ...(apiIssue.status !== undefined ? { status: apiIssue.status } : {}),
          ...(apiIssue.priority !== undefined ? { priority: apiIssue.priority } : {}),
          ...(apiIssue.assignee !== undefined ? { assignee: apiIssue.assignee } : {}),
          ...(apiIssue.assignees !== undefined ? { assignees: apiIssue.assignees } : {}),
          ...(apiIssue.assignees_users !== undefined ? { assignees_users: apiIssue.assignees_users } : {}),
          ...(apiIssue.reporter !== undefined ? { reporter: apiIssue.reporter } : {}),
          ...(apiIssue.labels !== undefined ? { labels: apiIssue.labels } : {}),
          ...(apiIssue.story_points !== undefined ? { storyPoints: apiIssue.story_points } : {}),
          ...(apiIssue.estimation_time !== undefined ? { estimationTime: apiIssue.estimation_time } : {}),
          ...(apiIssue.created_at !== undefined ? { createdAt: apiIssue.created_at } : {}),
          ...(apiIssue.updated_at !== undefined ? { updatedAt: apiIssue.updated_at } : {}),
          ...(apiIssue.due_date !== undefined ? { dueDate: apiIssue.due_date } : {}),
          ...(apiIssue.key !== undefined ? { key: apiIssue.key } : {}),
        };
      }));

      console.log('useJira: Issue updated successfully');
      return apiIssue;
    } catch (error) {
      console.error('Failed to update issue:', error);
      throw error;
    }
  };

  const deleteIssue = async (id: string) => {
    try {
      console.log('useJira: Deleting issue:', id);
      await apiService.deleteIssue(id);
      setIssues(prev => prev.filter(issue => issue.id !== id));
      console.log('useJira: Issue deleted successfully');
    } catch (error) {
      console.error('Failed to delete issue:', error);
      throw error;
    }
  };


  const refreshProjectIssues = async (projectId: string) => {
    try {
      console.log('useJira: Refreshing project issues for:', projectId);
      // Force refresh by clearing cache first
      await apiService.refreshIssues({ project_id: projectId });
      const projectIssues = await getIssuesByProject(projectId);
      // Update local state with fresh data
      setIssues(prev => [
        ...prev.filter(issue => issue.projectId !== projectId),
        ...projectIssues
      ]);
      console.log('useJira: Project issues refreshed successfully');
      return projectIssues;
    } catch (error) {
      console.error('Failed to refresh project issues:', error);
      throw error;
    }
  };

  const getIssuesByStatus = (status: string) => {
    return issues.filter(issue => issue.status === status);
  };

  const getIssuesByAssignee = (assigneeId: string) => {
    return issues.filter(issue => issue.assignees.includes(assigneeId));
  };

  const getIssuesBySprint = (sprintId: string) => {
    return issues.filter(issue => issue.sprint === sprintId);
  };

  const getProjectById = (id: string) => {
    return projects.find(project => project.id === id);
  };

  const getIssueById = (id: string) => {
    return issues.find(issue => issue.id === id);
  };

  const getUserById = (id: string) => {
    return users.find(user => user.id === id) || null;
  };

  const getDashboardStats = async () => {
    try {
      return await apiService.getDashboardStats();
    } catch (error) {
      console.error('Failed to get dashboard stats:', error);
      // Fallback to local calculation
      const totalIssues = issues.length;
      const assignedToMe = currentUser ? issues.filter(i => i.assignees.includes(currentUser.id)).length : 0;
      const reportedByMe = currentUser ? issues.filter(i => i.reporter === currentUser.id).length : 0;
      const inProgress = issues.filter(i => i.status === 'in-progress').length;
      const done = issues.filter(i => i.status === 'done').length;
      const bugs = issues.filter(i => i.type === 'bug').length;
      const stories = issues.filter(i => i.type === 'story').length;
      const tasks = issues.filter(i => i.type === 'task').length;

      return {
        totalIssues,
        assignedToMe,
        reportedByMe,
        inProgress,
        done,
        bugs,
        stories,
        tasks
      };
    }
  };

  const getDashboardIssueBreakdown = async (filters?: { year?: number; userId?: string; projectId?: string }) => {
    try {
      return await apiService.getDashboardIssueBreakdown(filters ?? {});
    } catch (error) {
      console.error('Failed to get dashboard issue breakdown:', error);

      const statusLabelsSeen = new Set<string>();
      const typeLabelsSeen = new Set<string>();

      const overallStatus: Record<string, number> = {};
      const overallTypes: Record<string, number> = {};

      const now = new Date();
      const yearFilter = filters?.year;
      const userFilter = filters?.userId;
      const projectFilter = filters?.projectId;

      const availableYearsSet = new Set<number>();
      issues.forEach(issue => {
        if (issue.dueDate) {
          const dueYear = new Date(issue.dueDate).getFullYear();
          if (!Number.isNaN(dueYear)) {
            availableYearsSet.add(dueYear);
          }
        }
      });

      const buildUserSummary = (user?: User | null) => {
        if (!user) return null;
        return {
          id: user.id,
          display_name: user.displayName,
          email_address: user.emailAddress,
          avatar_url: user.avatarUrl,
          active: user.active,
          groups: user.groups
        };
      };

      type ProjectUserEntry = {
        user: ReturnType<typeof buildUserSummary>;
        assigned: number;
        statusCounts: Record<string, number>;
        overdue: number;
      };

      type ProjectEntry = {
        project: { id: string; key: string; name: string };
        total: number;
        statusCounts: Record<string, number>;
        users: Map<string, ProjectUserEntry>;
      };

      type UserEntry = {
        user: ReturnType<typeof buildUserSummary>;
        assigned: number;
        statusCounts: Record<string, number>;
        projects: Map<string, number>;
        overdue: number;
      };

      const projectMap = new Map<string, ProjectEntry>();
      const userMap = new Map<string, UserEntry>();

      const projectSummaryMap = new Map<string, { id: string; key: string; name: string }>();
      projects.forEach(project => {
        projectSummaryMap.set(project.id, {
          id: project.id,
          key: project.key,
          name: project.name
        });
      });

      const buildProjectSummary = (projectId: string) => {
        if (!projectSummaryMap.has(projectId)) {
          projectSummaryMap.set(projectId, {
            id: projectId,
            key: 'N/A',
            name: 'Unknown Project'
          });
        }
        return projectSummaryMap.get(projectId)!;
      };

      const matchesFilters = (issue: Issue) => {
        if (yearFilter !== undefined) {
          if (!issue.dueDate) return false;
          const dueYear = new Date(issue.dueDate).getFullYear();
          if (Number.isNaN(dueYear) || dueYear !== yearFilter) return false;
        }

        if (projectFilter && issue.projectId !== projectFilter) {
          return false;
        }

        if (userFilter) {
          const assigneeIds = new Set<string>();
          if (issue.assignee) assigneeIds.add(issue.assignee);
          issue.assignees.forEach(id => assigneeIds.add(id));
          if (!assigneeIds.has(userFilter)) return false;
        }

        return true;
      };

      let filteredIssueCount = 0;
      let overdueTotal = 0;
      const overdueIssues: {
        id: string;
        key: string;
        summary: string;
        status: string;
        type: string;
        priority: string;
        due_date: string | null;
        project: { id: string | null; key: string; name: string };
        assignees: { id: string | null; name: string; email: string | undefined | null }[];
        days_overdue: number | null;
      }[] = [];

      issues.forEach(issue => {
        if (!matchesFilters(issue)) return;

        const statusKey = (issue.status || 'unknown').toLowerCase();
        const typeKey = (issue.type || 'task').toLowerCase();

        statusLabelsSeen.add(statusKey);
        typeLabelsSeen.add(typeKey);

        overallStatus[statusKey] = (overallStatus[statusKey] || 0) + 1;
        overallTypes[typeKey] = (overallTypes[typeKey] || 0) + 1;
        filteredIssueCount += 1;

        const projectId = issue.projectId;
        const projectSummary = buildProjectSummary(projectId);

        let projectEntry = projectMap.get(projectId);
        if (!projectEntry) {
          projectEntry = {
            project: { ...projectSummary },
            total: 0,
            statusCounts: {},
            users: new Map<string, ProjectUserEntry>()
          };
          projectMap.set(projectId, projectEntry);
        }

        projectEntry.total += 1;
        projectEntry.statusCounts[statusKey] = (projectEntry.statusCounts[statusKey] || 0) + 1;

        const assigneeIds = new Set<string>();
        if (issue.assignee) {
          assigneeIds.add(issue.assignee);
        }
        issue.assignees.forEach(id => assigneeIds.add(id));

        const dueDate = issue.dueDate ? new Date(issue.dueDate) : null;
        const isDone = statusKey === 'done' || statusKey === 'completed';
        const isOverdue = Boolean(dueDate && !isDone && dueDate < now);
        if (isOverdue) {
          overdueTotal += 1;
          const assigneeDetails = assigneeIds.size
            ? Array.from(assigneeIds).map(id => {
              const user = users.find(u => u.id === id) || null;
              return {
                id,
                name: user?.displayName ?? 'Unknown User',
                email: user?.emailAddress ?? null
              };
            })
            : [{ id: null, name: 'Unassigned', email: null }];

          const projectDetails = projects.find(project => project.id === projectId);
          overdueIssues.push({
            id: issue.id,
            key: issue.key,
            summary: issue.summary,
            status: issue.status,
            type: issue.type,
            priority: issue.priority,
            due_date: issue.dueDate ?? null,
            project: {
              id: projectDetails?.id ?? null,
              key: projectDetails?.key ?? 'N/A',
              name: projectDetails?.name ?? 'Unknown Project'
            },
            assignees: assigneeDetails,
            days_overdue: dueDate ? Math.max(0, Math.floor((now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24))) : null
          });
        }

        assigneeIds.forEach(assigneeId => {
          const user = users.find(u => u.id === assigneeId) || null;
          const summary = buildUserSummary(user);

          let userEntry = userMap.get(assigneeId);
          if (!userEntry) {
            userEntry = {
              user: summary,
              assigned: 0,
              statusCounts: {},
              projects: new Map<string, number>(),
              overdue: 0
            };
            userMap.set(assigneeId, userEntry);
          }

          userEntry.assigned += 1;
          userEntry.statusCounts[statusKey] = (userEntry.statusCounts[statusKey] || 0) + 1;
          userEntry.projects.set(projectId, (userEntry.projects.get(projectId) || 0) + 1);
          if (isOverdue) {
            userEntry.overdue += 1;
          }

          let projectUserEntry = projectEntry!.users.get(assigneeId);
          if (!projectUserEntry) {
            projectUserEntry = {
              user: summary,
              assigned: 0,
              statusCounts: {},
              overdue: 0
            };
            projectEntry!.users.set(assigneeId, projectUserEntry);
          }

          projectUserEntry.assigned += 1;
          projectUserEntry.statusCounts[statusKey] = (projectUserEntry.statusCounts[statusKey] || 0) + 1;
          if (isOverdue) {
            projectUserEntry.overdue += 1;
          }
        });
      });

      users.forEach(user => {
        if (!userMap.has(user.id)) {
          userMap.set(user.id, {
            user: buildUserSummary(user),
            assigned: 0,
            statusCounts: {},
            projects: new Map<string, number>(),
            overdue: 0
          });
        }
      });

      const knownStatusOrder = ['backlog', 'to-do', 'in-progress', 'in-review', 'done', 'on-hold'];
      const knownTypeOrder = ['story', 'task', 'bug', 'epic', 'subtask'];

      const buildOrderedLabels = (seen: Set<string>, known: string[]) => {
        const ordered = known.filter(label => seen.has(label));
        const remainder = Array.from(seen).filter(label => !known.includes(label)).sort();
        return [...ordered, ...remainder];
      };

      const normaliseCounts = (counts: Record<string, number>, labels: string[]) => {
        return labels.reduce<Record<string, number>>((acc, label) => {
          acc[label] = counts[label] || 0;
          return acc;
        }, {});
      };

      const statusLabels = buildOrderedLabels(statusLabelsSeen, knownStatusOrder);
      const typeLabels = buildOrderedLabels(typeLabelsSeen, knownTypeOrder);

      const projectsResponse = Array.from(projectMap.values())
        .map(projectEntry => {
          const usersList = Array.from(projectEntry.users.values())
            .map(userEntry => ({
              user: userEntry.user,
              assigned_issues: userEntry.assigned,
              status_counts: normaliseCounts(userEntry.statusCounts, statusLabels),
              overdue_issues: userEntry.overdue
            }))
            .sort((a, b) => b.assigned_issues - a.assigned_issues);

          return {
            project: projectEntry.project,
            total_issues: projectEntry.total,
            status_counts: normaliseCounts(projectEntry.statusCounts, statusLabels),
            users: usersList
          };
        })
        .sort((a, b) => b.total_issues - a.total_issues);

      const usersResponse = Array.from(userMap.values())
        .filter(userEntry => userEntry.assigned > 0 || userEntry.overdue > 0)
        .map(userEntry => {
          const projectCounts = Array.from(userEntry.projects.entries())
            .map(([projectId, count]) => ({
              project: buildProjectSummary(projectId),
              count
            }))
            .sort((a, b) => b.count - a.count);

          return {
            user: userEntry.user,
            assigned_issues: userEntry.assigned,
            status_counts: normaliseCounts(userEntry.statusCounts, statusLabels),
            projects: projectCounts,
            overdue_issues: userEntry.overdue
          };
        })
        .sort((a, b) => b.assigned_issues - a.assigned_issues);

      const availableYears = Array.from(availableYearsSet).sort((a, b) => b - a);

      overdueIssues.sort((a, b) => (b.days_overdue ?? 0) - (a.days_overdue ?? 0));

      return {
        total_issues: filteredIssueCount,
        overall_status: normaliseCounts(overallStatus, statusLabels),
        overall_types: normaliseCounts(overallTypes, typeLabels),
        status_labels: statusLabels,
        type_labels: typeLabels,
        projects: projectsResponse,
        users: usersResponse,
        overdue_total: overdueTotal,
        available_years: availableYears,
        overdue_issues: overdueIssues
      };
    }
  };

  const getCompletionTrends = async (filters: { year?: number; month?: number; userId?: string; projectId?: string } = {}) => {
    try {
      return await apiService.getCompletionTrends(filters);
    } catch (error) {
      console.error('Failed to get completion trends:', error);

      const pendingStatuses = new Set(['backlog', 'to-do', 'in-progress', 'in-review', 'on-hold']);
      const completedStatuses = new Set(['done', 'completed']);

      const now = new Date();
      const targetYear = filters.year ?? now.getFullYear();
      const defaultMonth = now.getMonth() + 1;
      const activeMonth = filters.month ?? defaultMonth;

      const projectMap = new Map(projects.map(project => [project.id, project]));

      const getIssueDate = (issue: Issue) => {
        const candidate = issue.dueDate || issue.updatedAt || issue.createdAt;
        if (!candidate) return null;
        const dt = new Date(candidate);
        return Number.isNaN(dt.getTime()) ? null : dt;
      };

      const matchesFilters = (issue: Issue) => {
        if (filters.projectId && issue.projectId !== filters.projectId) {
          return false;
        }

        const reference = getIssueDate(issue);
        if (!reference || reference.getFullYear() !== targetYear) {
          return false;
        }

        if (filters.userId) {
          const assigneeSet = new Set<string>();
          if (issue.assignee) assigneeSet.add(issue.assignee);
          issue.assignees.forEach(id => assigneeSet.add(id));
          if (!assigneeSet.has(filters.userId)) {
            return false;
          }
        }

        return true;
      };

      const availableMonthsSet = new Set<number>();
      const availableProjectsMap = new Map<string, { id: string; key: string; name: string }>();
      const monthlyCounts = new Map<number, { pending: number; completed: number }>();
      const weeklyCounts = new Map<number, { pending: number; completed: number }>();

      issues.forEach(issue => {
        if (!matchesFilters(issue)) return;

        const reference = getIssueDate(issue);
        if (!reference) return;

        const monthIndex = reference.getMonth() + 1;
        availableMonthsSet.add(monthIndex);

        const status = (issue.status || 'unknown').toLowerCase();
        const bucket = completedStatuses.has(status) ? 'completed' : 'pending';

        const monthBucket = monthlyCounts.get(monthIndex) || { pending: 0, completed: 0 };
        monthBucket[bucket] += 1;
        monthlyCounts.set(monthIndex, monthBucket);

        if (monthIndex === activeMonth) {
          const weekIndex = Math.floor((reference.getDate() - 1) / 7) + 1;
          const weeklyBucket = weeklyCounts.get(weekIndex) || { pending: 0, completed: 0 };
          weeklyBucket[bucket] += 1;
          weeklyCounts.set(weekIndex, weeklyBucket);
        }

        const projectDetails = projectMap.get(issue.projectId);
        if (projectDetails && !availableProjectsMap.has(projectDetails.id)) {
          availableProjectsMap.set(projectDetails.id, {
            id: projectDetails.id,
            key: projectDetails.key,
            name: projectDetails.name
          });
        }
      });

      const monthly = Array.from({ length: 12 }, (_, idx) => {
        const monthIdx = idx + 1;
        const bucket = monthlyCounts.get(monthIdx) || { pending: 0, completed: 0 };
        const label = new Date(targetYear, monthIdx - 1, 1).toLocaleString('default', { month: 'short' });
        return {
          label,
          pending: bucket.pending,
          completed: bucket.completed
        };
      });

      const daysInMonth = new Date(targetYear, activeMonth, 0).getDate();
      const totalWeeks = Math.max(1, Math.ceil(daysInMonth / 7));
      const weekly = Array.from({ length: totalWeeks }, (_, idx) => {
        const weekIdx = idx + 1;
        const bucket = weeklyCounts.get(weekIdx) || { pending: 0, completed: 0 };
        return {
          label: `Week ${weekIdx}`,
          pending: bucket.pending,
          completed: bucket.completed
        };
      });

      const availableMonths = Array.from(availableMonthsSet).sort((a, b) => a - b);
      const availableProjects = Array.from(availableProjectsMap.values()).sort((a, b) => a.key.localeCompare(b.key));

      return {
        year: targetYear,
        month: activeMonth,
        monthly,
        weekly,
        available_months: availableMonths.length ? availableMonths : [activeMonth],
        available_projects: availableProjects
      };
    }
  };

  const searchIssues = async (query: string) => {
    try {
      return await apiService.searchIssues(query);
    } catch (error) {
      console.error('Failed to search issues:', error);
      // Fallback to local search
      const lowercaseQuery = query.toLowerCase();
      return issues.filter(issue =>
        issue.key.toLowerCase().includes(lowercaseQuery) ||
        issue.summary.toLowerCase().includes(lowercaseQuery) ||
        issue.description.toLowerCase().includes(lowercaseQuery) ||
        issue.labels.some(label => label.toLowerCase().includes(lowercaseQuery))
      );
    }
  };

  const addMessage = (messageData: Omit<Message, 'id' | 'timestamp'>) => {
    const newMessage: Message = {
      ...messageData,
      id: Date.now().toString(),
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, newMessage]);
    return newMessage;
  };

  const getMessagesByIssue = (issueId: string) => {
    return messages.filter(message => message.issueId === issueId);
  };

  // Chat functions
  const addChatMessage = (issueId: string, content: string, mentions?: string[], fileUrl?: string, fileName?: string) => {
    if (!currentUser) return;

    const issue = issues.find(i => i.id === issueId);
    if (!issue?.chatId) return;

    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      chatId: issue.chatId,
      userId: currentUser.id,
      content,
      timestamp: new Date().toISOString(),
      type: fileUrl ? 'file' : 'text',
      fileUrl,
      fileName,
      mentions
    };

    setChats(prev => prev.map(chat =>
      chat.id === issue.chatId ? {
        ...chat,
        messages: [...chat.messages, newMessage],
        updatedAt: new Date().toISOString()
      } : chat
    ));

    return newMessage;
  };

  const getChatByIssueId = (issueId: string) => {
    const issue = issues.find(i => i.id === issueId);
    if (!issue?.chatId) return null;
    return chats.find(chat => chat.id === issue.chatId);
  };

  const getChatMessages = (issueId: string) => {
    const chat = getChatByIssueId(issueId);
    return chat?.messages || [];
  };

  // ─── Manager Dashboard API wrappers ─────────────────────────────────────

  const getManagerOverview = async (projectId?: string) => {
    try {
      return await apiService.getManagerOverview(projectId);
    } catch (error) {
      console.error('Failed to fetch manager overview:', error);
      return null;
    }
  };

  const getManagerProjects = async () => {
    try {
      return await apiService.getManagerProjects();
    } catch (error) {
      console.error('Failed to fetch manager projects:', error);
      return [];
    }
  };

  const getManagerTeam = async (projectId?: string) => {
    try {
      return await apiService.getManagerTeam(projectId);
    } catch (error) {
      console.error('Failed to fetch manager team:', error);
      return [];
    }
  };

  const getManagerTasks = async (filters: {
    projectId?: string;
    status?: string;
    priority?: string;
    assigneeId?: string;
    sortBy?: string;
    sortOrder?: string;
    page?: number;
    pageSize?: number;
  } = {}) => {
    try {
      return await apiService.getManagerTasks(filters);
    } catch (error) {
      console.error('Failed to fetch manager tasks:', error);
      return { tasks: [], total: 0, page: 1, page_size: 25, total_pages: 0 };
    }
  };

  const getManagerMonthlyTrends = async (projectId?: string, months: number = 12) => {
    try {
      return await apiService.getManagerMonthlyTrends(projectId, months);
    } catch (error) {
      console.error('Failed to fetch monthly trends:', error);
      return [];
    }
  };

  const getManagerWeeklyTrends = async (projectId?: string, weeks: number = 12) => {
    try {
      return await apiService.getManagerWeeklyTrends(projectId, weeks);
    } catch (error) {
      console.error('Failed to fetch weekly trends:', error);
      return [];
    }
  };

  const getManagerProjectDetail = async (projectId: string) => {
    try {
      return await apiService.getManagerProjectDetail(projectId);
    } catch (error) {
      console.error('Failed to fetch project detail:', error);
      return null;
    }
  };

  const getSprintBoardData = async (projectId?: string) => {
    try {
      return await apiService.getSprintBoardData(projectId);
    } catch (error) {
      console.error('Failed to fetch sprint board data:', error);
      return { sprints: [], backlog: [], backlog_count: 0 };
    }
  };

  return {
    projects,
    issues,
    users,
    sprints,
    messages,
    chats,
    currentUser,
    isLoading,
    error,
    addSprint,
    updateSprint,
    deleteSprint,
    startSprint,
    completeSprint,
    addIssueToSprint,
    removeIssueFromSprint,
    getSprintStats,
    addProject,
    updateProject,
    deleteProject,
    addIssue,
    updateIssue,
    deleteIssue,
    getIssuesByProject,
    refreshProjectIssues,
    getIssuesByStatus,
    getIssuesByAssignee,
    getIssuesBySprint,
    getProjectById,
    getIssueById,
    getUserById,
    getDashboardStats,
    getDashboardIssueBreakdown,
    getCompletionTrends,
    searchIssues,
    addMessage,
    getMessagesByIssue,
    addChatMessage,
    getChatByIssueId,
    getChatMessages,
    // Manager Dashboard
    getManagerOverview,
    getManagerProjects,
    getManagerTeam,
    getManagerTasks,
    getManagerMonthlyTrends,
    getManagerWeeklyTrends,
    getManagerProjectDetail,
    getSprintBoardData,
  };
};
